import './styles.scss';

import { Button, Card } from 'react-bootstrap';
import HistoryFilters, { useReportHistoryFilters } from '@/features/DriveTimeTool/components/History/HistoryFilters';
import { RouteStatusEnum, convertDtrLocationToPotentialLocation, getHistory } from '../../api/services';
import { createNotification, getClasses } from '@/utils';
import { useMemo, useRef } from 'react';

import CardHeader from '@/features/DriveTimeTool/components/CardHeader';
import HistoryCard from '../HistoryCard';
import { LoadingBlur } from '@/components/LoadingSpinner';
import Tippy from '@tippyjs/react';
import Toast from '@/models/Toast';
import { formatFullAddress } from '../../utils/locations';
import { useDriveTimeTool } from '@/features/DriveTimeTool';
import useInterval from '@/hooks/useInterval';

const POLLING_DELAY = 15000; // 15 seconds

const History = (): JSX.Element => {
  const [state, setState] = useDriveTimeTool(({ state, setState }) => [
    {
      history: state.history,
      loading: state.loading.history,
      expandHistory: state.expandHistory,
    },
    setState,
  ]);
  const historyRef = useRef<HTMLDivElement>(null);

  const routes = useMemo(() => state.history.flatMap((item) => item.routes), [state.history]);

  useInterval(async () => {
    const isProcessing = routes.find((item) => item.status === RouteStatusEnum.Running || item.status === RouteStatusEnum.InQueue);
    if (!isProcessing) return;
    const status = routes.reduce(
      (acc, item) => {
        acc[item.id] = item.status;
        return acc;
      },
      {} as Record<string, RouteStatusEnum>
    );
    const filters = useReportHistoryFilters.getState().state;
    // get grouped location ids by address to search
    const state = useDriveTimeTool.getState().state;
    const puLocationIds = state.groupedLocationIdsByAddress.get(
      formatFullAddress(convertDtrLocationToPotentialLocation(state.locations.get(filters.puLocationId)))
    );
    const doLocationIds = state.groupedLocationIdsByAddress.get(
      formatFullAddress(convertDtrLocationToPotentialLocation(state.locations.get(filters.doLocationId)))
    );
    const res = await getHistory({
      ...filters,
      puLocationId: puLocationIds,
      doLocationId: doLocationIds,
    });
    setState((current) => ({ ...current, history: res }));
    const newRoutes = res.flatMap((item) => item.routes);
    // Track status changes and notify user
    if (status !== undefined) {
      newRoutes.forEach((record) => {
        const currentStatus = status[record.id];
        if (currentStatus && currentStatus !== record.status) {
          switch (record.status) {
            case RouteStatusEnum.Completed:
              createNotification(`Your drive time report is ready!`, Toast.Type.SUCCESS, 'Drive Time Report Complete');
              break;
            case RouteStatusEnum.Failed:
              createNotification(
                `We couldn't generate your drive time report. Please try again`,
                Toast.Type.DANGER,
                'Drive Time Report Error'
              );
              break;
          }
        }
      });
    }

    // Update status tracking
    const newStatus: Record<string, RouteStatusEnum> = {};
    newRoutes.forEach((record) => {
      newStatus[record.id] = record.status;
    });
  }, POLLING_DELAY);

  return (
    <div className={getClasses('History', state.expandHistory ? 'expanded' : '')} ref={historyRef}>
      <Card>
        <CardHeader icon="fas fa-search" subtitle="Manage your drive time analysis reports" info={`${history.length} Reports Total`}>
          Report History
          <div className="History-Info">
            <span>
              {state.history.length} Report{state.history.length !== 1 ? 's' : ''}
            </span>
            <Button
              variant="icon"
              className="History-ExpandBtn"
              onClick={() => setState((current) => ({ ...current, expandHistory: !current.expandHistory }))}
            >
              <Tippy content={state.expandHistory ? 'Collapse History' : 'Expand History'} className="tippy-dark">
                <i className={state.expandHistory ? 'sv sv-contract2' : 'sv sv-expand2'} />
              </Tippy>
            </Button>
          </div>
        </CardHeader>
        <HistoryFilters />
        <Card.Body>
          <div className="HistoryList">
            {state.history.length === 0 && (
              <h5 className="h-100 w-100 d-flex justify-content-center align-items-center m-0 p-0">No reports found</h5>
            )}
            {state.history.length > 0 && (
              <>
                <LoadingBlur loading={state.loading} />
                {state.history.map((historyItem, index: number) => (
                  <HistoryCard data={historyItem} key={historyItem.manifestHistoryId || index} />
                ))}
              </>
            )}
          </div>
        </Card.Body>
      </Card>
    </div>
  );
};

export default History;
